import React, { useState, useEffect, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useRole } from '../hooks/useRole';
import { usePagination } from '../hooks/usePagination';
import { Pagination, ErrorState } from '../components';
import auditLogService from '../api/services/auditLogService';
import { PageContainer, PageHeaderRow } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { Badge } from '../components/Badge';
import { StyledSelect } from '../components/FormStyles';

// --- Constants ---

const ENTITY_TYPES = [
  'All',
  'FormSubmission',
  'WorkOrder',
  'User',
  'Organization',
  'FormSchema',
  'PdfTemplate',
  'Customer',
  'Location',
  'Permission',
  'Auth',
  'License',
  'ReportSettings',
  'NotificationRules',
];

const ACTIONS = [
  'All',
  'Create',
  'Update',
  'Delete',
  'StatusChange',
  'BulkDelete',
  'Activate',
  'Deactivate',
  'Duplicate',
  'SetDefault',
  'LoginSuccess',
  'LoginFailed',
  'PasswordReset',
  'ForgotPassword',
  'AssignWorkOrder',
  'RoleChange',
  'AssignPermissions',
  'RevokePermissions',
];

const ACTION_COLORS = {
  Create: { bg: '#22c55e20', color: '#22c55e' },
  Update: { bg: '#3b82f620', color: '#3b82f6' },
  Delete: { bg: '#ef444420', color: '#ef4444' },
  BulkDelete: { bg: '#ef444420', color: '#ef4444' },
  StatusChange: { bg: '#f59e0b20', color: '#f59e0b' },
  Activate: { bg: '#22c55e20', color: '#22c55e' },
  Deactivate: { bg: '#6b728020', color: '#6b7280' },
  Duplicate: { bg: '#8b5cf620', color: '#8b5cf6' },
  SetDefault: { bg: '#06b6d420', color: '#06b6d4' },
  LoginSuccess: { bg: '#a855f720', color: '#a855f7' },
  LoginFailed: { bg: '#ef444420', color: '#ef4444' },
  PasswordReset: { bg: '#a855f720', color: '#a855f7' },
  ForgotPassword: { bg: '#a855f720', color: '#a855f7' },
  AssignWorkOrder: { bg: '#f59e0b20', color: '#f59e0b' },
  RoleChange: { bg: '#3b82f620', color: '#3b82f6' },
  AssignPermissions: { bg: '#22c55e20', color: '#22c55e' },
  RevokePermissions: { bg: '#ef444420', color: '#ef4444' },
};

// --- Styled Components ---

const FilterRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled(StyledSelect)`
  min-width: 160px;
`;

const FilterInput = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const SearchInput = styled(FilterInput)`
  min-width: 220px;
  flex: 1;
  max-width: 320px;
`;

const DateInput = styled(FilterInput)`
  min-width: 150px;

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: ${props => props.theme.colors.background === '#ffffff' ? 'none' : 'invert(0.8)'};
  }
`;

const ClickableRow = styled(Tr)`
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const ActionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.$bgColor || props.theme.colors.backgroundSecondary};
  color: ${props => props.$textColor || props.theme.colors.text};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 0.7rem;
  font-weight: 700;
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

const TruncatedCell = styled(Td)`
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// --- Modal Detail Styles ---

const DetailModalContent = styled(ModalContent)`
  max-width: 720px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const MetaValue = styled.span`
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  word-break: break-word;
`;

const ChangesTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

const ChangesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
`;

const ChangesThead = styled.thead`
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const ChangesTh = styled.th`
  text-align: left;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ChangesTr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ChangesTd = styled.td`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.default};
  word-break: break-word;
  max-width: 220px;
`;

const OldValue = styled(ChangesTd)`
  background-color: #ef444410;
  color: ${props => props.theme.colors.text};
`;

const NewValue = styled(ChangesTd)`
  background-color: #22c55e10;
  color: ${props => props.theme.colors.text};
`;

const CloseButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  font-weight: 500;
  white-space: nowrap;
`;

// --- Helpers ---

const formatTimestamp = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getActionBadgeColors = (action) => {
  return ACTION_COLORS[action] || { bg: '#6b728020', color: '#6b7280' };
};

const parseChanges = (details) => {
  if (!details) return [];
  // details may be a string (JSON) or already parsed array/object
  let parsed = details;
  if (typeof details === 'string') {
    try {
      parsed = JSON.parse(details);
    } catch {
      return [];
    }
  }
  // Handle array of { fieldName, oldValue, newValue }
  if (Array.isArray(parsed)) return parsed;
  // Handle $values pattern
  if (parsed?.$values && Array.isArray(parsed.$values)) return parsed.$values;
  return [];
};

// --- Component ---

export const AuditLogSection = () => {
  const theme = useTheme();
  const { isSuperAdmin, isAdmin } = useRole();

  // Filter state
  const [entityType, setEntityType] = useState('All');
  const [action, setAction] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Data state
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Detail modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Pagination
  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(25);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const params = { ...paginationParams };

      if (entityType !== 'All') params.entityType = entityType;
      if (action !== 'All') params.action = action;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (searchTerm.trim()) params.searchTerm = searchTerm.trim();

      const response = await auditLogService.getLogs(params);

      // Handle both response.items and response.data.items patterns
      const { items, totalCount: total } = extractPagedData(response);
      setLogs(items);
      setTotalCount(total);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setFetchError(err?.response?.data?.message || 'Failed to load audit logs. Please check your connection and try again.');
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [paginationParams, entityType, action, fromDate, toDate, searchTerm, extractPagedData, setTotalCount]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    resetPage();
  }, [entityType, action, fromDate, toDate, searchTerm, resetPage]);

  const handleRowClick = async (log) => {
    setSelectedLog(log);
    setDetailData(null);
    setDetailLoading(true);

    try {
      const detail = await auditLogService.getLogById(log.id);
      setDetailData(detail);
    } catch (err) {
      console.error('Failed to fetch audit log detail:', err);
      toast.error('Failed to load audit log details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedLog(null);
    setDetailData(null);
  };

  const getChangesCount = (log) => {
    const changes = parseChanges(log.details || log.changes);
    return changes.length;
  };

  const renderFilterBar = () => (
    <FilterRow>
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
        <FilterLabel>Entity:</FilterLabel>
        <FilterSelect value={entityType} onChange={e => setEntityType(e.target.value)}>
          {ENTITY_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </FilterSelect>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
        <FilterLabel>Action:</FilterLabel>
        <FilterSelect value={action} onChange={e => setAction(e.target.value)}>
          {ACTIONS.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </FilterSelect>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
        <FilterLabel>From:</FilterLabel>
        <DateInput
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
        <FilterLabel>To:</FilterLabel>
        <DateInput
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
        />
      </div>

      <SearchInput
        type="text"
        placeholder="Search logs..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
      />
    </FilterRow>
  );

  const renderTable = () => {
    if (loading) {
      return (
        <LoadingState>
          Loading audit logs...
        </LoadingState>
      );
    }

    if (fetchError) {
      return (
        <ErrorState
          theme={theme}
          title="Failed to load audit logs"
          message={fetchError}
          onRetry={fetchLogs}
        />
      );
    }

    if (logs.length === 0) {
      return (
        <EmptyState>
          No audit logs found matching the current filters.
        </EmptyState>
      );
    }

    return (
      <TableContainer>
        <Table>
          <Thead>
            <tr>
              <Th>Timestamp</Th>
              <Th>User</Th>
              <Th>Action</Th>
              <Th>Entity Type</Th>
              <Th>Entity Name</Th>
              <Th>Description</Th>
              <Th>Changes</Th>
            </tr>
          </Thead>
          <Tbody>
            {logs.map(log => {
              const actionColors = getActionBadgeColors(log.action);
              const changesCount = getChangesCount(log);

              return (
                <ClickableRow key={log.id} onClick={() => handleRowClick(log)}>
                  <Td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                    {formatTimestamp(log.timestamp || log.createdAt)}
                  </Td>
                  <TruncatedCell title={log.userEmail || log.userName || '-'}>
                    {log.userEmail || log.userName || '-'}
                  </TruncatedCell>
                  <Td>
                    <ActionBadge $bgColor={actionColors.bg} $textColor={actionColors.color}>
                      {log.action}
                    </ActionBadge>
                  </Td>
                  <Td>{log.entityType || '-'}</Td>
                  <TruncatedCell title={log.entityName || '-'}>
                    {log.entityName || '-'}
                  </TruncatedCell>
                  <TruncatedCell title={log.description || '-'}>
                    {log.description || '-'}
                  </TruncatedCell>
                  <Td>
                    {changesCount > 0 && (
                      <CountBadge>{changesCount}</CountBadge>
                    )}
                  </Td>
                </ClickableRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  const renderDetailModal = () => {
    if (!selectedLog) return null;

    const data = detailData || selectedLog;
    const actionColors = getActionBadgeColors(data.action);
    const changes = parseChanges(data.details || data.changes);

    return (
      <ModalOverlay $isOpen={!!selectedLog} onClick={closeModal}>
        <DetailModalContent onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitleRow>
              <ActionBadge $bgColor={actionColors.bg} $textColor={actionColors.color}>
                {data.action}
              </ActionBadge>
              <span style={{ fontWeight: 600, color: theme.colors.text }}>
                {data.entityType}
              </span>
              {data.entityName && (
                <span style={{ color: theme.colors.muted }}>
                  - {data.entityName}
                </span>
              )}
            </ModalTitleRow>
            <CloseButton onClick={closeModal}>Close</CloseButton>
          </ModalHeader>

          <ModalBody>
            {detailLoading ? (
              <LoadingState>Loading details...</LoadingState>
            ) : (
              <>
                <MetaGrid>
                  <MetaItem>
                    <MetaLabel>User</MetaLabel>
                    <MetaValue>{data.userEmail || data.userName || '-'}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Timestamp</MetaLabel>
                    <MetaValue>{formatTimestamp(data.timestamp || data.createdAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>IP Address</MetaLabel>
                    <MetaValue>{data.ipAddress || '-'}</MetaValue>
                  </MetaItem>
                  {data.entityId && (
                    <MetaItem>
                      <MetaLabel>Entity ID</MetaLabel>
                      <MetaValue>{data.entityId}</MetaValue>
                    </MetaItem>
                  )}
                </MetaGrid>

                {data.description && (
                  <div style={{ marginBottom: theme.spacing.lg }}>
                    <MetaLabel>Description</MetaLabel>
                    <MetaValue style={{ display: 'block', marginTop: 4 }}>
                      {data.description}
                    </MetaValue>
                  </div>
                )}

                {changes.length > 0 && (
                  <>
                    <ChangesTitle>Field-Level Changes ({changes.length})</ChangesTitle>
                    <div style={{ overflowX: 'auto' }}>
                      <ChangesTable>
                        <ChangesThead>
                          <tr>
                            <ChangesTh>Field Name</ChangesTh>
                            <ChangesTh>Old Value</ChangesTh>
                            <ChangesTh>New Value</ChangesTh>
                          </tr>
                        </ChangesThead>
                        <tbody>
                          {changes.map((change, idx) => (
                            <ChangesTr key={idx}>
                              <ChangesTd style={{ fontWeight: 500 }}>
                                {change.fieldName || change.field || change.propertyName || '-'}
                              </ChangesTd>
                              <OldValue>
                                {change.oldValue !== null && change.oldValue !== undefined
                                  ? String(change.oldValue)
                                  : '-'}
                              </OldValue>
                              <NewValue>
                                {change.newValue !== null && change.newValue !== undefined
                                  ? String(change.newValue)
                                  : '-'}
                              </NewValue>
                            </ChangesTr>
                          ))}
                        </tbody>
                      </ChangesTable>
                    </div>
                  </>
                )}
              </>
            )}
          </ModalBody>
        </DetailModalContent>
      </ModalOverlay>
    );
  };

  return (
    <PageContainer>
        <PageHeaderRow>
          <h2 style={{ margin: 0, color: theme.colors.text }}>Audit Log</h2>
        </PageHeaderRow>

        {renderFilterBar()}
        {renderTable()}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={goToPage}
          itemLabel="entries"
        />

        {renderDetailModal()}
    </PageContainer>
  );
};

export const AuditLogPage = () => (
  <Layout currentPath="/settings">
    <AuditLogSection />
  </Layout>
);
